import { useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import type { PharmacyDailyRow } from './types';

/**
 * Net pharmacy sales per day.
 *
 * ── Why a column chart, and why only one series ──
 * The job is magnitude across ordered days — which day was big, which was
 * quiet, is the week trending up. That is a column chart. It is deliberately a
 * SINGLE series (net = billed less refunds), not billed-and-refunds side by
 * side: two series here would need a categorical palette and a legend to
 * answer a question the reader is not actually asking. Billed, refunded and
 * the count stay in the tooltip and in the table beneath, so nothing is lost —
 * the chart just stops competing with itself. One series also means no legend
 * box: the heading already says what is plotted.
 *
 * ── Colour ──
 * One hue, the hospital's own `--teal`. A per-tenant runtime brand colour
 * cannot be pinned to a validated hex, and substituting a fixed palette hue
 * would make this the one element on the page that ignores the hospital's
 * branding. The categorical checks (chroma floor, CVD separation) do not apply
 * to a single-series chart; what does apply — contrast against the surface —
 * the default #0B5FD6 passes at ≥3:1. Every value is also present as text in
 * the table below, so nothing is conveyed by colour alone.
 *
 * Mark specs follow the house rules: columns capped at 24px so the band keeps
 * its air, 4px rounded cap with a square foot on the baseline, a 2px surface
 * gap between neighbours, hairline recessive gridlines, and a value label only
 * on the best day rather than on every column.
 */

const CHART_H = 132;   // plot area only, excluding the axis strip
const MAX_BAR_W = 24;
const GAP = 2;         // the surface gap between touching columns

/** Rounds an axis maximum up to a clean 1/2/5 × 10ⁿ so ticks read as real numbers. */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = 10 ** exp;
  const n = v / base;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * base;
}

const compact = (v: number) =>
  v >= 1e7 ? `${(v / 1e7).toFixed(1)}Cr`
  : v >= 1e5 ? `${(v / 1e5).toFixed(1)}L`
  : v >= 1e3 ? `${Math.round(v / 1e3)}k`
  // Below 10, keep one decimal: on a day with no sales the axis tops out at 1,
  // and rounding the midpoint would print "1" twice — two different gridlines
  // wearing the same number.
  : v >= 10 ? String(Math.round(v))
  : String(Math.round(v * 10) / 10);

const rupees = (v: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

export default function DailySalesChart({ rows }: { rows: PharmacyDailyRow[] }) {
  const [hover, setHover] = useState<number | null>(null);

  const data = useMemo(
    () => rows.map((r) => ({
      date: r.date,
      net: Math.max(0, parseFloat(r.net) || 0),
      total: parseFloat(r.total) || 0,
      refunds: parseFloat(r.refunds) || 0,
      bills: r.bill_count,
    })),
    [rows]
  );

  const max = useMemo(() => niceMax(Math.max(...data.map((d) => d.net), 0)), [data]);
  const peak = useMemo(() => data.reduce((best, d, i) => (d.net > (data[best]?.net ?? -1) ? i : best), 0), [data]);
  const everythingZero = data.every((d) => d.net === 0);

  if (!data.length) return null;

  const active = hover != null ? data[hover] : null;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
          Net sales per day
        </div>
        {/* The hovered day's full breakdown reads out here rather than in a
            floating tooltip: the row is only ~130px tall, so a popover would
            cover the very columns being compared. */}
        <div className="text-[11.5px] tabular-nums text-ink-muted">
          {active ? (
            <>
              <span className="font-semibold text-ink">
                {new Date(active.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
              </span>
              {' · '}{rupees(active.net)} net
              {active.refunds > 0 && <> · {rupees(active.refunds)} refunded</>}
              {' · '}{active.bills} bill{active.bills === 1 ? '' : 's'}
            </>
          ) : (
            <span className="opacity-70">Hover a day for its breakdown</span>
          )}
        </div>
      </div>

      <div className="relative" style={{ height: CHART_H }}>
        {/* Gridlines — hairline, solid, one step off the surface, behind the
            data. They carry the values no column is labelled with. */}
        {[0, 0.5, 1].map((f) => (
          <div
            key={f}
            className="pointer-events-none absolute inset-x-0 flex items-center"
            // translateY(50%) centres the row ON its value. `bottom` alone puts
            // the row's bottom edge there, so the 0 gridline would float half a
            // line-height above the baseline the columns actually stand on.
            style={{ bottom: `${f * 100}%`, transform: 'translateY(50%)' }}
          >
            <span className="w-9 shrink-0 pr-1.5 text-right text-[9.5px] tabular-nums text-ink-muted/70">
              {f === 0 ? '0' : compact(max * f)}
            </span>
            <span className="h-px flex-1 bg-border" />
          </div>
        ))}

        {/* Columns */}
        <div
          className="absolute inset-y-0 right-0 flex items-end"
          style={{ left: 36, gap: GAP }}
          onMouseLeave={() => setHover(null)}
        >
          {data.map((d, i) => {
            const h = everythingZero ? 0 : (d.net / max) * 100;
            const isPeak = i === peak && d.net > 0;
            return (
              <div
                key={d.date}
                className="group relative flex h-full min-w-0 flex-1 cursor-default items-end justify-center"
                onMouseEnter={() => setHover(i)}
                // Deliberately not focusable: a month-long range would put 30 tab
                // stops between the heading and the next control. The day-by-day
                // table below is the keyboard and screen-reader path to the same
                // numbers, which is why it sits beside this chart rather than
                // behind it.
                aria-label={`${new Date(d.date).toLocaleDateString('en-IN')}: ${rupees(d.net)} net from ${d.bills} bills`}
              >
                {/* A full-height hit target — the column itself can be 2px tall
                    on a quiet day, which is not hoverable. */}
                <div
                  className={cn(
                    'w-full max-w-[24px] rounded-t-[4px] transition-opacity',
                    hover != null && hover !== i && 'opacity-45'
                  )}
                  style={{
                    height: `${Math.max(h, d.net > 0 ? 2 : 0)}%`,
                    minHeight: d.net > 0 ? 2 : 0,
                    background: 'var(--teal)',
                    maxWidth: MAX_BAR_W,
                  }}
                />
                {/* Direct-label the best day only. A number on every column is
                    the fastest way to make a chart unreadable. */}
                {isPeak && (
                  <span
                    className="pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[9.5px] font-semibold tabular-nums text-ink-muted"
                    style={{ bottom: `calc(${Math.max(h, 2)}% + 3px)` }}
                  >
                    {compact(d.net)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Date axis. Ticks thin out as the range grows so labels never collide —
          measured by count rather than by pixel width, which is enough here
          because every label is the same 6-character shape. */}
      <div className="mt-1 flex" style={{ marginLeft: 36, gap: GAP }}>
        {data.map((d, i) => {
          const every = data.length <= 10 ? 1 : data.length <= 20 ? 3 : 7;
          const isEdge = i === 0 || i === data.length - 1;
          // The last day is always labelled, so a computed tick landing right
          // beside it is dropped — otherwise a 30-day range prints "29 Jul
          // 30 Jul" jammed together while the rest of the axis is evenly spaced.
          const crowdsTheEnd = !isEdge && data.length - 1 - i < Math.ceil(every / 2);
          const show = isEdge || (i % every === 0 && !crowdsTheEnd);
          return (
            <div key={d.date} className="min-w-0 flex-1 text-center">
              {show && (
                <span className="text-[9.5px] tabular-nums text-ink-muted/80">
                  {new Date(d.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
